import { Controller } from "stimulus";

// Just Like Live: client-side event-clock sync.
// Server supplies start/end/duration/serverTime (+ optional playback offset);
// browser owns position math. No polling, WebSockets, or per-viewer jobs.
export default class extends Controller {
  static targets = [
    "video",
    "endedOverlay",
    "unmuteButton",
    "unmuteBarButton",
    "unmuteOverlay",
    "audioHint",
    "debugPanel",
    "statusLabel",
  ];

  // Stimulus 2 only accepts constructor types here (String/Number/Boolean/…).
  // Object-style `{ type, default }` throws at application.load() and aborts the pack.
  static values = {
    startsAt: String,
    endsAt: String,
    durationSeconds: Number,
    serverTime: String,
    playbackOffsetSeconds: Number,
    playbackPulseUrl: String,
    driftIntervalMs: Number,
    driftToleranceSeconds: Number,
    debug: Boolean,
  };

  connect() {
    this.localMonotonicAtLoad = performance.now();
    this.serverTimeAtLoadMs = this.parseTimeMs(this.serverTimeValue, Date.now());
    this.startsAtMs = this.parseTimeMs(this.startsAtValue, NaN);
    this.endsAtMs = this.parseTimeMs(this.endsAtValue, NaN);
    this.durationSeconds = Number(this.durationSecondsValue) || 0;
    // Absent / negative offset means "derive from startsAt" (Stimulus 2 Number is 0 when unset).
    this.serverPlaybackOffsetSeconds =
      this.hasPlaybackOffsetSecondsValue && this.playbackOffsetSecondsValue >= 0
        ? Number(this.playbackOffsetSecondsValue)
        : null;
    this.driftIntervalMs =
      this.hasDriftIntervalMsValue && this.driftIntervalMsValue > 0
        ? this.driftIntervalMsValue
        : 60000;
    this.driftToleranceSeconds =
      this.hasDriftToleranceSecondsValue && this.driftToleranceSecondsValue > 0
        ? this.driftToleranceSecondsValue
        : 2.5;

    this._ended = false;
    this._resyncing = false;
    this._soundUnlocked = false;
    this._ready = false;
    this._initialSyncDone = false;
    this._playbackStarted = false;
    this._startInFlight = false;

    this.videoEl = this.hasVideoTarget ? this.videoTarget : null;
    if (!this.videoEl) return;

    this.boundVisibility = () => this.onVisibilityOrFocus();
    this.boundOnline = () => this.resync("online");
    this.boundPageShow = (event) => {
      if (event.persisted) this.resync("pageshow", { force: true });
    };
    this.boundWaiting = () => {
      this._wasBuffering = true;
      this.stopPlaybackTracking({ flush: true });
    };
    this.boundPlaying = () => {
      this._playbackStarted = true;
      this.startPlaybackTracking();
      if (this._wasBuffering) {
        this._wasBuffering = false;
        this.resync("buffering-resume");
      }
      // Autoplay / play() can leave currentTime at 0 after an earlier seek.
      if (!this._initialSyncDone) {
        this._initialSyncDone = true;
        this.resync("playing-initial", { force: true });
      }
    };
    this.boundLoadedMetadata = () => this.onReady();
    this.boundCanPlay = () => {
      if (!this._ready) this.onReady();
      else this.resync("canplay", { force: !this._initialSyncDone });
    };
    // A fixed-clock session has nothing left to play once the media ends, and
    // play() on a finished element restarts it at 0. Reaching the end early
    // means the declared duration is longer than the file.
    this.boundEnded = () => this.endSession("media-ended");
    this.boundPause = () => this.stopPlaybackTracking({ flush: true });

    document.addEventListener("visibilitychange", this.boundVisibility);
    window.addEventListener("focus", this.boundVisibility);
    window.addEventListener("online", this.boundOnline);
    window.addEventListener("pageshow", this.boundPageShow);
    this.videoEl.addEventListener("waiting", this.boundWaiting);
    this.videoEl.addEventListener("playing", this.boundPlaying);
    this.videoEl.addEventListener("loadedmetadata", this.boundLoadedMetadata);
    this.videoEl.addEventListener("canplay", this.boundCanPlay);
    this.videoEl.addEventListener("ended", this.boundEnded);
    this.videoEl.addEventListener("pause", this.boundPause);

    if (this.videoEl.readyState >= 1) this.onReady();

    // Never leave a late joiner on a frozen frame with zero controls.
    this._stuckTimer = setTimeout(() => {
      if (this._ended || !this.videoEl) return;
      if (!this._ready && this.videoEl.readyState >= 1) this.onReady();
      if (this.videoEl.paused) this.showTapToPlay();
    }, 1800);
  }

  disconnect() {
    this.stopPlaybackTracking({ flush: true });
    document.removeEventListener("visibilitychange", this.boundVisibility);
    window.removeEventListener("focus", this.boundVisibility);
    window.removeEventListener("online", this.boundOnline);
    window.removeEventListener("pageshow", this.boundPageShow);
    if (this.videoEl) {
      this.videoEl.removeEventListener("waiting", this.boundWaiting);
      this.videoEl.removeEventListener("playing", this.boundPlaying);
      this.videoEl.removeEventListener("loadedmetadata", this.boundLoadedMetadata);
      this.videoEl.removeEventListener("canplay", this.boundCanPlay);
      this.videoEl.removeEventListener("ended", this.boundEnded);
      this.videoEl.removeEventListener("pause", this.boundPause);
    }
    if (this._driftTimer) clearInterval(this._driftTimer);
    if (this._endTimer) clearTimeout(this._endTimer);
    if (this._debugTimer) clearInterval(this._debugTimer);
    if (this._stuckTimer) clearTimeout(this._stuckTimer);
    if (this._playWatchdog) clearTimeout(this._playWatchdog);
    if (this._postStartTimers) {
      this._postStartTimers.forEach((id) => clearTimeout(id));
    }
  }

  parseTimeMs(value, fallback) {
    if (value == null || value === "") return fallback;
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : fallback;
  }

  estimatedNowMs() {
    return this.serverTimeAtLoadMs + (performance.now() - this.localMonotonicAtLoad);
  }

  expectedPositionSeconds(nowMs = this.estimatedNowMs()) {
    let raw;
    if (Number.isFinite(this.startsAtMs)) {
      raw = (nowMs - this.startsAtMs) / 1000;
    } else if (this.serverPlaybackOffsetSeconds != null) {
      // startsAt failed to parse — advance from server-computed offset.
      raw =
        this.serverPlaybackOffsetSeconds +
        (performance.now() - this.localMonotonicAtLoad) / 1000;
    } else {
      return 0;
    }

    if (raw < 0) return 0;
    if (this.durationSeconds > 0) return Math.min(raw, this.durationSeconds);
    return raw;
  }

  // True once the media itself has nothing left to play. Resuming from here
  // would restart the recording at 0:00.
  atMediaEnd() {
    if (!this.videoEl) return false;
    if (this.videoEl.ended) return true;
    return (
      this.durationSeconds > 0 &&
      this.videoEl.currentTime >= this.durationSeconds - 0.25
    );
  }

  sessionEnded(nowMs = this.estimatedNowMs()) {
    if (Number.isFinite(this.endsAtMs)) return nowMs >= this.endsAtMs;
    if (this.durationSeconds > 0 && Number.isFinite(this.startsAtMs)) {
      return nowMs >= this.startsAtMs + this.durationSeconds * 1000;
    }
    return false;
  }

  onReady() {
    if (this._ready || !this.videoEl) return;
    this._ready = true;

    if (this.sessionEnded()) {
      this.endSession("already-ended");
      return;
    }

    this.scheduleExactEndTimer();
    // Seek BEFORE play so late joiners never start at 0 from autoplay race.
    this.seekThenStartPlayback();
    this.lockControls();
    this.schedulePostStartResyncs();

    if (this._driftTimer) clearInterval(this._driftTimer);
    this._driftTimer = setInterval(
      () => this.driftCheck(),
      this.driftIntervalMs
    );

    if (this.debugValue) {
      if (this._debugTimer) clearInterval(this._debugTimer);
      this._debugTimer = setInterval(() => this.updateDebug(), 500);
      this.updateDebug();
    }
  }

  seekThenStartPlayback() {
    const expected = this.expectedPositionSeconds();
    const afterSeek = () => this.startPlaybackPreferringSound();

    if (!Number.isFinite(expected) || expected <= 0.25) {
      afterSeek();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.videoEl.removeEventListener("seeked", onSeeked);
      afterSeek();
    };
    const onSeeked = () => finish();
    this.videoEl.addEventListener("seeked", onSeeked);
    this.seekTo(expected);
    // seeked may not fire if currentTime is already near target.
    setTimeout(finish, 300);
  }

  schedulePostStartResyncs() {
    this._postStartTimers = [400, 1200, 2500].map((ms) =>
      setTimeout(() => this.resync(`post-start-${ms}`, { force: true }), ms)
    );
  }

  scheduleExactEndTimer() {
    if (this._endTimer) clearTimeout(this._endTimer);
    if (!Number.isFinite(this.endsAtMs)) return;

    const msUntilEnd = this.endsAtMs - this.estimatedNowMs();
    if (msUntilEnd <= 0) {
      this.endSession("end-timer-immediate");
      return;
    }
    this._endTimer = setTimeout(() => this.endSession("end-timer"), msUntilEnd);
  }

  consumeWantSoundFlag() {
    try {
      if (sessionStorage.getItem("jll_want_sound") === "1") {
        sessionStorage.removeItem("jll_want_sound");
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("sound") === "1") return true;
    } catch (e) {
      /* ignore */
    }
    // Arrival at /watch is always from a Join/Go To Event navigation.
    return true;
  }

  playVideo(muted) {
    if (!this.videoEl) return Promise.reject(new Error("no-video"));
    this.videoEl.muted = !!muted;
    this.videoEl.volume = 1;
    const playPromise = this.videoEl.play();
    if (playPromise && typeof playPromise.then === "function") {
      return playPromise;
    }
    return this.videoEl.paused
      ? Promise.reject(new Error("play-noop"))
      : Promise.resolve();
  }

  startPlaybackPreferringSound() {
    if (this._startInFlight || this._ended || !this.videoEl) return;
    this._startInFlight = true;
    const preferSound = this.consumeWantSoundFlag();

    const afterPlaying = (withSound) => {
      this._startInFlight = false;
      this._playbackStarted = true;
      this.resync("after-play", { force: true });
      if (withSound) {
        this._soundUnlocked = true;
        this.hideUnmuteControls();
      } else {
        this.showUnmuteControls();
      }
      this.armPlayWatchdog();
    };

    // Unmuted first (join gesture / ?sound=1), then muted, then Tap to play.
    const attempt = preferSound
      ? this.playVideo(false).catch(() => this.playVideo(true))
      : this.playVideo(true);

    attempt
      .then(() => afterPlaying(!this.videoEl.muted))
      .catch(() => {
        this._startInFlight = false;
        this.showTapToPlay();
      });
  }

  armPlayWatchdog() {
    if (this._playWatchdog) clearTimeout(this._playWatchdog);
    this._playWatchdog = setTimeout(() => {
      if (this._ended || !this.videoEl) return;
      if (this.videoEl.paused) this.showTapToPlay();
    }, 600);
  }

  setPrimaryActionLabel(label, iconClass) {
    if (!this.hasUnmuteButtonTarget) return;
    this.unmuteButtonTarget.innerHTML =
      `<i class="${iconClass}" aria-hidden="true"></i> ${label}`;
  }

  showUnmuteControls() {
    this.setPrimaryActionLabel("Tap to unmute", "fas fa-volume-up");
    if (this.hasUnmuteButtonTarget) {
      this.unmuteButtonTarget.classList.add("is-visible");
    }
    if (this.hasUnmuteBarButtonTarget) {
      this.unmuteBarButtonTarget.classList.add("is-visible");
    }
    if (this.hasUnmuteOverlayTarget) {
      this.unmuteOverlayTarget.classList.add("is-visible");
    }
    if (this.hasAudioHintTarget) {
      this.audioHintTarget.textContent = "Tap to unmute";
    }
  }

  showTapToPlay() {
    if (this._ended) return;
    this.setPrimaryActionLabel("Tap to play", "fas fa-play");
    if (this.hasUnmuteButtonTarget) {
      this.unmuteButtonTarget.classList.add("is-visible");
    }
    if (this.hasUnmuteBarButtonTarget) {
      this.unmuteBarButtonTarget.classList.add("is-visible");
    }
    if (this.hasUnmuteOverlayTarget) {
      this.unmuteOverlayTarget.classList.add("is-visible");
    }
    if (this.hasAudioHintTarget) {
      this.audioHintTarget.textContent = "Tap to play";
    }
  }

  hideUnmuteControls() {
    if (this.hasUnmuteButtonTarget) {
      this.unmuteButtonTarget.classList.remove("is-visible");
      this.setPrimaryActionLabel("Tap to unmute", "fas fa-volume-up");
    }
    if (this.hasUnmuteBarButtonTarget) {
      this.unmuteBarButtonTarget.classList.remove("is-visible");
    }
    if (this.hasUnmuteOverlayTarget) {
      this.unmuteOverlayTarget.classList.remove("is-visible");
    }
    if (this.hasAudioHintTarget) {
      this.audioHintTarget.textContent = "";
    }
  }

  // User gesture: start (or resume) playback with sound when possible.
  unmute() {
    if (!this.videoEl || this._ended) return;
    this.resync("user-gesture", { force: true });
    this.playVideo(false)
      .then(() => {
        this._soundUnlocked = true;
        this._playbackStarted = true;
        this.hideUnmuteControls();
        this.resync("unmute", { force: true });
      })
      .catch(() => {
        this.playVideo(true)
          .then(() => {
            this._playbackStarted = true;
            this.showUnmuteControls();
            this.resync("unmute-muted", { force: true });
          })
          .catch(() => this.showTapToPlay());
      });
  }

  toggleFullscreen() {
    const root = this.element;
    if (!document.fullscreenElement) {
      if (root.requestFullscreen) root.requestFullscreen().catch(() => {});
    } else if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  }

  lockControls() {
    this.videoEl.removeAttribute("controls");
    this.videoEl.controls = false;
    this.videoEl.addEventListener("ratechange", () => {
      if (this.videoEl.playbackRate !== 1) this.videoEl.playbackRate = 1;
    });
    this.videoEl.addEventListener("seeking", () => {
      if (this._resyncing || this._ended) return;
      const expected = this.expectedPositionSeconds();
      if (Math.abs(this.videoEl.currentTime - expected) > this.driftToleranceSeconds) {
        this.seekTo(expected);
      }
    });
    this.videoEl.addEventListener("keydown", (e) => {
      const blocked = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", " ", "j", "k", "l", "Home", "End"];
      if (blocked.includes(e.key)) e.preventDefault();
    });
  }

  driftCheck() {
    if (this._ended || this.sessionEnded()) {
      this.endSession("drift-end");
      return;
    }
    const expected = this.expectedPositionSeconds();
    const actual = this.videoEl.currentTime || 0;
    const drift = Math.abs(actual - expected);
    if (drift > this.driftToleranceSeconds) {
      this.seekTo(expected);
      this.logDebug(`drift-correct ${drift.toFixed(2)}s`);
    }
    if (this.videoEl.paused && !this._startInFlight && !this.atMediaEnd()) {
      this.videoEl.play().catch(() => this.showTapToPlay());
    }
  }

  onVisibilityOrFocus() {
    if (document.visibilityState && document.visibilityState !== "visible") {
      this.stopPlaybackTracking({ flush: true });
      return;
    }
    if (this.videoEl && !this.videoEl.paused) this.startPlaybackTracking();
    this.resync("visibility", { force: true });
  }

  startPlaybackTracking() {
    if (!this.hasPlaybackPulseUrlValue || !this.playbackPulseUrlValue) return;
    if (this._ended || !this.videoEl || this.videoEl.paused) return;
    if (document.visibilityState && document.visibilityState !== "visible") return;

    if (!this._playbackPulseTimer) {
      this.sendPlaybackPulse();
      this._playbackPulseTimer = setInterval(() => this.sendPlaybackPulse(), 15000);
    }
  }

  stopPlaybackTracking({ flush = false } = {}) {
    if (flush && this._playbackPulseTimer) this.sendPlaybackPulse({ force: true });
    if (this._playbackPulseTimer) clearInterval(this._playbackPulseTimer);
    this._playbackPulseTimer = null;
  }

  sendPlaybackPulse({ force = false } = {}) {
    if (!this.hasPlaybackPulseUrlValue || !this.playbackPulseUrlValue) return;
    if (!force) {
      if (this._ended || !this.videoEl || this.videoEl.paused) return;
      if (document.visibilityState && document.visibilityState !== "visible") return;
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
    const headers = { Accept: "application/json" };
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
    fetch(this.playbackPulseUrlValue, {
      method: "POST",
      headers,
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => {});
  }

  resync(reason = "manual", { force = false } = {}) {
    if (this._ended || !this.videoEl) return;
    if (this.sessionEnded()) {
      this.endSession(reason);
      return;
    }
    const expected = this.expectedPositionSeconds();
    if (!Number.isFinite(expected)) return;

    const actual = this.videoEl.currentTime || 0;
    const drift = Math.abs(actual - expected);
    if (force || drift > this.driftToleranceSeconds) {
      this.seekTo(expected);
      this.logDebug(`resync:${reason} -> ${expected.toFixed(2)}s (was ${actual.toFixed(2)}s)`);
    }
    if (!this.videoEl.paused) return;
    if (this.atMediaEnd()) return;
    this.videoEl.play().catch(() => {
      this.showTapToPlay();
    });
  }

  seekTo(seconds) {
    if (!this.videoEl || this._ended) return;
    if (!Number.isFinite(seconds)) return;
    this._resyncing = true;
    try {
      const clamped = Math.max(0, seconds);
      if (this.durationSeconds > 0) {
        this.videoEl.currentTime = Math.min(clamped, this.durationSeconds);
      } else {
        this.videoEl.currentTime = clamped;
      }
      this.videoEl.playbackRate = 1;
    } finally {
      setTimeout(() => {
        this._resyncing = false;
      }, 350);
    }
  }

  endSession(reason = "ended") {
    if (this._ended) return;
    this.stopPlaybackTracking({ flush: true });
    this._ended = true;
    if (this._driftTimer) clearInterval(this._driftTimer);
    if (this._endTimer) clearTimeout(this._endTimer);
    if (this._stuckTimer) clearTimeout(this._stuckTimer);
    if (this._playWatchdog) clearTimeout(this._playWatchdog);
    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.removeAttribute("src");
      this.videoEl.load();
    }
    if (this.hasEndedOverlayTarget) {
      this.endedOverlayTarget.classList.remove("d-none");
    }
    if (this.hasVideoTarget) {
      this.videoTarget.classList.add("d-none");
    }
    this.hideUnmuteControls();
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = "Ended";
    }
    this.logDebug(`ended:${reason}`);
  }

  logDebug(message) {
    if (!this.debugValue) return;
    // eslint-disable-next-line no-console
    console.log(`[simulated-live] ${message}`);
    this.updateDebug(message);
  }

  updateDebug(lastMessage) {
    if (!this.hasDebugPanelTarget) return;
    const now = this.estimatedNowMs();
    const expected = this.expectedPositionSeconds(now);
    const actual = this.videoEl && !this._ended ? this.videoEl.currentTime || 0 : 0;
    this.debugPanelTarget.textContent = [
      `state: ${this._ended ? "ended" : "live"}`,
      `startsAt: ${this.startsAtValue}`,
      `startsAtMs finite: ${Number.isFinite(this.startsAtMs)}`,
      `serverOffset: ${this.serverPlaybackOffsetSeconds}`,
      `estimatedNow: ${new Date(now).toISOString()}`,
      `expected: ${Number.isFinite(expected) ? expected.toFixed(2) : "NaN"}s`,
      `actual: ${actual.toFixed(2)}s`,
      `drift: ${Number.isFinite(expected) ? (actual - expected).toFixed(2) : "NaN"}s`,
      `endsIn: ${
        Number.isFinite(this.endsAtMs)
          ? Math.max(0, (this.endsAtMs - now) / 1000).toFixed(1)
          : "?"
      }s`,
      lastMessage ? `last: ${lastMessage}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }
}
